/* =============================================================================
   HOUSE OF ATURAÈ — theme behaviour
   -----------------------------------------------------------------------------
   Vanilla custom elements, no framework, no dependencies. Every interactive
   element degrades to a working link or form when JavaScript is unavailable:
   the cart drawer falls back to /cart, filters fall back to a GET form, the
   variant picker falls back to a native <select>.

   Cart and filter updates re-render markup on the server through Shopify's
   Section Rendering API, so prices, discounts and translations are never
   reformatted on the client.
   ========================================================================== */

(() => {
  'use strict';

  const HOA = (window.HOA = window.HOA || {});
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Where scroll-driven animation is supported the reveals are driven entirely
  // by CSS on the compositor (see the @supports block in tailwind.css), so the
  // observer below must stand down or both would drive the same elements.
  const hasScrollTimeline =
    typeof CSS !== 'undefined' &&
    CSS.supports &&
    CSS.supports('animation-timeline', 'view()');

  /* --- Utilities --------------------------------------------------------- */

  /** Announce a message through the single polite live region. */
  const announce = (message) => {
    const region = document.getElementById('a11y-status');
    if (!region || !message) return;
    // Clearing first guarantees repeat messages are re-announced.
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  /** Fetch one or more rendered sections. Returns a map of id -> HTML string. */
  const fetchSections = async (ids, searchParams = '') => {
    const params = new URLSearchParams(searchParams);
    params.set('sections', ids.join(','));
    const response = await fetch(`${HOA.routes.root}?${params.toString()}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
    return response.json();
  };

  /** Replace an element's inner markup from an HTML string, matching by selector. */
  const replaceInner = (selector, html) => {
    const target = document.querySelector(selector);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(selector);
    if (source) target.innerHTML = source.innerHTML;
  };

  /* --- Body scroll lock ---------------------------------------------------
     Reference counted, because a drawer can be opened while another is
     closing. The scroll position is saved and restored by hand: the locked
     body is position:fixed, so without the offset every close would jump the
     page back to the top.
     --------------------------------------------------------------------- */

  let lockDepth = 0;
  let lockedAt = 0;

  const lockScroll = () => {
    if (lockDepth++ > 0) return;
    lockedAt = window.scrollY;
    document.body.style.top = `-${lockedAt}px`;
    document.body.classList.add('u-scroll-locked');
  };

  const unlockScroll = (force = false) => {
    if (force) lockDepth = 0;
    else if (--lockDepth > 0) return;
    lockDepth = 0;
    document.body.classList.remove('u-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, lockedAt);
  };

  /** Last-resort release: if nothing is open, the page must be scrollable. */
  const reconcileScrollLock = () => {
    if (!document.querySelector('[data-open="true"]')) unlockScroll(true);
  };

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, summary, [tabindex]:not([tabindex="-1"])';

  const focusableWithin = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );

  /* --- Scroll reveal ------------------------------------------------------
     Applied only after JS confirms IntersectionObserver support, so content is
     never hidden from users whose JS fails to load. Skipped entirely when the
     user has asked for reduced motion.
     --------------------------------------------------------------------- */

  const initReveal = () => {
    // .js-reveal fades and rises. .js-draw only receives the state attribute —
    // used by the arc, which must not fade in (it is structural, not decor)
    // but does need to know when it has entered so it can draw itself.
    if (hasScrollTimeline) return;

    const targets = document.querySelectorAll('.js-reveal, .js-draw');
    if (!targets.length) return;

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.setAttribute('data-reveal', 'in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-reveal', 'in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => {
      el.setAttribute('data-reveal', 'pending');
      observer.observe(el);
    });
  };

  /* --- Sticky header ------------------------------------------------------
     The header sits transparent over a full-bleed hero and solidifies on
     scroll. Uses a sentinel element rather than a scroll listener so it costs
     nothing on the main thread.
     --------------------------------------------------------------------- */

  class HoaHeader extends HTMLElement {
    connectedCallback() {
      this.sentinel = document.getElementById('header-sentinel');
      this.overlay = this.hasAttribute('data-overlay');

      if (!this.sentinel || !('IntersectionObserver' in window)) {
        this.setAttribute('data-stuck', 'true');
        return;
      }

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.setAttribute('data-stuck', String(!entry.isIntersecting));
        },
        { threshold: 0 }
      );
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  /* --- Drawer -------------------------------------------------------------
     A modal side panel. Traps focus, closes on Escape and backdrop click, and
     returns focus to whatever opened it.
     --------------------------------------------------------------------- */

  class HoaDrawer extends HTMLElement {
    // Live lookups, never cached. The cart drawer's markup is replaced
    // wholesale on every cart change, so a reference captured at connect time
    // points at a detached node — which is why tapping the backdrop stopped
    // closing the drawer once anything had been added to the cart.
    get panel() {
      return this.querySelector('[data-drawer-panel]');
    }

    get backdrop() {
      return this.querySelector('[data-drawer-backdrop]');
    }

    connectedCallback() {
      this.opener = null;

      this.addEventListener('click', (event) => {
        // Matched by attribute rather than identity, for the same reason.
        if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-drawer-backdrop]')) {
          this.close();
        }
      });

      this.onKeydown = this.onKeydown.bind(this);

      // Any control anywhere in the document can open this drawer by id.
      document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      if (this.getAttribute('data-open') === 'true') return;
      this.setAttribute('data-open', 'true');
      this.panel?.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      // Focus the first meaningful control, not the close button, where possible.
      window.requestAnimationFrame(() => {
        const preferred = this.querySelector('[data-drawer-autofocus]');
        const fallback = focusableWithin(this.panel || this)[0];
        (preferred || fallback || this.panel)?.focus();
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (this.getAttribute('data-open') !== 'true') return;
      this.removeAttribute('data-open');
      this.panel?.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(this.panel || this);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* --- Cart -------------------------------------------------------------- */

  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  const refreshCart = async () => {
    try {
      const sections = await fetchSections(CART_SECTIONS);
      if (sections['cart-drawer']) replaceInner('#CartDrawer', sections['cart-drawer']);
      if (sections['cart-icon-bubble']) replaceInner('#CartCount', sections['cart-icon-bubble']);
    } catch (error) {
      console.error('[HOA] cart refresh failed', error);
    }
  };

  class HoaCartDrawer extends HoaDrawer {}

  /** Add-to-cart form. Posts via fetch and opens the drawer on success. */
  class HoaProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-cart-error]');
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.toggleAttribute('aria-busy', busy);
      this.button.disabled = busy;
      this.button.querySelector('[data-spinner]')?.classList.toggle('hidden', !busy);
    }

    /**
     * Brief confirmation on the button itself. The drawer opening is the real
     * feedback; this just stops the click feeling unacknowledged. Purely
     * visual — the spoken confirmation already went through the live region.
     */
    flashSuccess() {
      const check = this.button?.querySelector('[data-check]');
      const label = this.button?.querySelector('[data-add-label]');
      if (!check || !label) return;

      check.classList.remove('hidden');
      label.classList.add('opacity-0');

      window.setTimeout(() => {
        check.classList.add('hidden');
        label.classList.remove('opacity-0');
      }, 1400);
    }

    async onSubmit(event) {
      event.preventDefault();
      this.setBusy(true);
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      try {
        const body = new FormData(this.form);
        body.append('sections', CART_SECTIONS.join(','));

        const response = await fetch(HOA.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();

        if (data.status) {
          // Shopify returns `status` only on error (e.g. insufficient stock).
          throw new Error(data.description || data.message || HOA.strings.cartError);
        }

        if (data.sections?.['cart-drawer']) {
          replaceInner('#CartDrawer', data.sections['cart-drawer']);
        }
        if (data.sections?.['cart-icon-bubble']) {
          replaceInner('#CartCount', data.sections['cart-icon-bubble']);
        }

        announce(HOA.strings.addedToCart);
        this.flashSuccess();
        document.getElementById('CartDrawer')?.open(this.button);
      } catch (error) {
        const message = error.message || HOA.strings.cartError;
        if (this.errorEl) {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        }
        announce(message);
      } finally {
        this.setBusy(false);
      }
    }
  }

  /** Quantity stepper that also drives cart line updates when given a line index. */
  class HoaQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.line = this.getAttribute('data-line');
      if (!this.input) return;

      this.querySelectorAll('[data-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.getAttribute('data-qty-step'));
          const min = Number(this.input.min || 0);
          const next = Math.max(min, Number(this.input.value || 0) + step);
          if (next === Number(this.input.value)) return;
          this.input.value = String(next);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      if (this.line) {
        this.input.addEventListener('change', () => this.updateLine(Number(this.input.value)));
      }
    }

    async updateLine(quantity) {
      this.setAttribute('data-busy', 'true');
      try {
        const response = await fetch(HOA.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(this.line),
            quantity,
            sections: CART_SECTIONS.join(','),
          }),
        });
        const data = await response.json();

        if (data.sections?.['cart-drawer']) {
          replaceInner('#CartDrawer', data.sections['cart-drawer']);
        }
        if (data.sections?.['cart-icon-bubble']) {
          replaceInner('#CartCount', data.sections['cart-icon-bubble']);
        }
        // The full cart page has no drawer to swap, so reload its section.
        if (document.getElementById('CartPage')) {
          const sections = await fetchSections(['main-cart']);
          if (sections['main-cart']) replaceInner('#CartPage', sections['main-cart']);
        }
        announce(HOA.strings.cartUpdated || '');
      } catch (error) {
        console.error('[HOA] cart update failed', error);
        announce(HOA.strings.cartError);
      } finally {
        this.removeAttribute('data-busy');
      }
    }
  }

  /* --- Variant picker -----------------------------------------------------
     Reads the product's variant JSON, keeps the URL and the hidden variant id
     in sync, and updates price and availability by re-rendering the relevant
     section server-side.
     --------------------------------------------------------------------- */

  class HoaVariants extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section');
      this.productUrl = this.getAttribute('data-url');
      this.idInput = document.querySelector(`#variant-id-${this.sectionId}`);

      const script = this.querySelector('[data-variant-data]');
      if (!script) return;

      try {
        this.variants = JSON.parse(script.textContent);
      } catch (error) {
        console.error('[HOA] variant data unreadable', error);
        return;
      }

      this.addEventListener('change', this.onChange.bind(this));
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const select = fieldset.querySelector('select');
        return select ? select.value : null;
      });
    }

    onChange() {
      const selected = this.selectedOptions;
      const match = this.variants.find((variant) =>
        variant.options.every((option, index) => option === selected[index])
      );

      if (!match) {
        this.setUnavailable();
        return;
      }

      if (this.idInput) this.idInput.value = match.id;

      // Keep the address bar shareable without a navigation.
      if (this.productUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }

      this.render(match.id);
    }

    setUnavailable() {
      const button = document.querySelector(`#add-to-cart-${this.sectionId}`);
      if (!button) return;
      button.disabled = true;
      const label = button.querySelector('[data-add-label]');
      if (label) label.textContent = HOA.strings.unavailable;
    }

    async render(variantId) {
      try {
        const sections = await fetchSections([`main-product`], `variant=${variantId}`);
        const html = sections['main-product'];
        if (!html) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');

        // querySelectorAll, paired by index: price appears twice on a product
        // page — in the purchase panel and in the sticky mobile bar — and only
        // updating the first would leave the bar quoting the wrong variant.
        ['[data-variant-price]', '[data-variant-cta]', '[data-variant-inventory]'].forEach((selector) => {
          const next = parsed.querySelectorAll(selector);
          const current = document.querySelectorAll(selector);
          current.forEach((el, i) => {
            if (next[i]) el.innerHTML = next[i].innerHTML;
          });
        });

        // Availability has to reach every add control, wherever it lives.
        const nextButton = parsed.querySelector(`#add-to-cart-${this.sectionId}`);
        if (nextButton) {
          document
            .querySelectorAll(`#add-to-cart-${this.sectionId}, hoa-sticky-buy button[name="add"]`)
            .forEach((btn) => {
              btn.disabled = nextButton.disabled;
            });
        }
      } catch (error) {
        console.error('[HOA] variant render failed', error);
      }
    }
  }

  /* --- Collection filters -------------------------------------------------
     Enhances a plain GET form. Without JS the form still submits and filters
     work; with JS the results swap in place and the URL stays shareable.
     --------------------------------------------------------------------- */

  class HoaFacets extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.debounce = null;
      this.form.addEventListener('change', () => {
        window.clearTimeout(this.debounce);
        this.debounce = window.setTimeout(() => this.apply(), 250);
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });
    }

    async apply() {
      const params = new URLSearchParams(new FormData(this.form)).toString();
      this.setAttribute('data-busy', 'true');

      try {
        const sections = await fetchSections(['main-collection'], params);
        const html = sections['main-collection'];
        if (html) {
          replaceInner('#CollectionResults', html);
          initReveal();
        }
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);

        const count = document.querySelector('[data-result-count]')?.textContent?.trim();
        if (count) announce(count);
      } catch (error) {
        // Fall back to a real navigation rather than leaving stale results.
        window.location.search = params;
      } finally {
        this.removeAttribute('data-busy');
      }
    }
  }

  /* --- Line reveal --------------------------------------------------------
     Display headings rise line by line out of a clipping wrapper — the
     couture convention for a high-contrast serif.

     Lines are measured, not guessed: words are wrapped, their offsetTop
     compared, then regrouped. Measurement waits on document.fonts.ready
     because a fallback face wraps at different points than the real one,
     which would band the wrong words together.
     --------------------------------------------------------------------- */

  const buildLineGroups = (el) => {
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return null;

    // Probe pass: one span per word so offsetTop reveals the line breaks.
    const probe = document.createDocumentFragment();
    const wordEls = text.split(' ').map((word, i) => {
      const span = document.createElement('span');
      span.textContent = word;
      probe.append(span);
      if (i < text.split(' ').length - 1) probe.append(document.createTextNode(' '));
      return span;
    });

    el.textContent = '';
    el.append(probe);

    const lines = [];
    let top = null;
    wordEls.forEach((span) => {
      const y = span.offsetTop;
      if (top === null || Math.abs(y - top) < 2) {
        if (top === null) {
          top = y;
          lines.push([]);
        }
        lines[lines.length - 1].push(span.textContent);
      } else {
        top = y;
        lines.push([span.textContent]);
      }
    });

    return lines.map((words) => words.join(' '));
  };

  const initLineReveal = (root = document) => {
    const targets = root.querySelectorAll('[data-line-reveal]:not([data-split])');
    if (!targets.length) return;

    targets.forEach((el) => {
      el.setAttribute('data-split', '');

      // Reduced motion keeps the text exactly as authored — no wrappers.
      if (prefersReducedMotion.matches) return;

      const original = el.textContent;
      const lines = buildLineGroups(el);

      if (!lines || lines.length === 0) {
        el.textContent = original;
        return;
      }

      el.textContent = '';
      lines.forEach((line, index) => {
        const clip = document.createElement('span');
        clip.className = 'line-clip';
        const inner = document.createElement('span');
        inner.textContent = line;

        // Above-the-fold headings animate on time (see the [data-motion=load]
        // block in tailwind.css), so they take a delay even where scroll
        // timelines are supported.
        const onLoad = !!el.closest('[data-motion="load"]');

        // A scroll timeline has no notion of time, so the stagger has to be
        // expressed as a shifted range rather than a delay.
        if (hasScrollTimeline && !onLoad) {
          const start = index * 7;
          inner.style.animationRange = `entry ${start}% entry ${start + 55}%`;
        } else {
          inner.style.animationDelay = `${index * 90}ms`;
        }
        clip.append(inner);
        el.append(clip);

        // Descenders would be clipped once the animation has settled.
        inner.addEventListener(
          'animationend',
          () => {
            clip.style.overflow = 'visible';
          },
          { once: true }
        );
      });
    });
  };

  /* --- Product slider -----------------------------------------------------
     The scrolling itself is native CSS scroll-snap: momentum on touch, real
     keyboard scrolling, and it still works with JavaScript disabled. This
     element only adds the arrows and the progress rail.
     --------------------------------------------------------------------- */

  class HoaSlider extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-slider-rail]');
      if (!this.rail) return;

      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.bar = this.querySelector('[data-slider-bar]');
      this.ticking = false;

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));

      this.rail.addEventListener('scroll', () => this.onScroll(), { passive: true });
      window.addEventListener('resize', () => this.update(), { passive: true });

      // Wait a frame so item widths are resolved before first measurement.
      window.requestAnimationFrame(() => this.update());
    }

    /** Width of one item plus its gap — the natural scroll increment. */
    get stride() {
      const item = this.rail.firstElementChild;
      if (!item) return this.rail.clientWidth;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap || '0') || 0;
      return item.getBoundingClientRect().width + gap;
    }

    get maxScroll() {
      return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    }

    step(direction) {
      // Advance by whole items, but never more than a screenful at a time.
      const perView = Math.max(1, Math.floor(this.rail.clientWidth / this.stride));
      this.rail.scrollBy({
        left: this.stride * perView * direction,
        behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const max = this.maxScroll;
      const left = this.rail.scrollLeft;

      // 1px tolerance: sub-pixel layout leaves scrollLeft just shy of max.
      if (this.prev) this.prev.disabled = left <= 1;
      if (this.next) this.next.disabled = left >= max - 1;

      // A rail with nothing to scroll should not advertise controls.
      this.toggleAttribute('data-static', max <= 1);

      if (this.bar) {
        const visible = this.rail.clientWidth / this.rail.scrollWidth;
        const progress = max > 0 ? left / max : 0;
        this.bar.style.width = `${Math.min(100, visible * 100)}%`;
        this.bar.style.transform = `translateX(${progress * ((1 / visible - 1) * 100)}%)`;
      }
    }
  }

  /* --- Sticky buy bar -----------------------------------------------------
     Revealed once the real add-to-cart button has scrolled above the
     viewport. Watching the button itself rather than a scroll offset means it
     never covers the control it stands in for, whatever the page length.
     --------------------------------------------------------------------- */

  class HoaStickyBuy extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.getAttribute('data-observe') || '');

      // No target or no observer: leave it hidden rather than permanently
      // parked over the page.
      if (!target || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          const scrolledPast = entry.boundingClientRect.top < 0;
          this.toggleAttribute('data-show', !entry.isIntersecting && scrolledPast);
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  /* --- Media fade ---------------------------------------------------------
     Images fade in once decoded. Applied progressively: if JS never runs the
     images are simply visible, because the opacity:0 is only meaningful once
     the class is paired with the data attribute this sets.
     --------------------------------------------------------------------- */

  const initMediaFade = (root = document) => {
    root.querySelectorAll('img.media-fade:not([data-loaded])').forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        img.dataset.loaded = 'true';
        return;
      }
      const mark = () => (img.dataset.loaded = 'true');
      img.addEventListener('load', mark, { once: true });
      img.addEventListener('error', mark, { once: true });
    });
  };

  /* --- View transitions ---------------------------------------------------
     view-transition-name must be unique per document, so it cannot be baked
     into every product card. Instead the clicked card claims the shared name
     immediately before navigation; the product page always names its first
     gallery image the same thing, and the browser morphs between them.
     --------------------------------------------------------------------- */

  const VT_NAME = 'product-media';

  const initViewTransitions = () => {
    if (!('startViewTransition' in document)) return;

    const clearNames = () => {
      document.querySelectorAll('[data-vt-claimed]').forEach((el) => {
        el.style.viewTransitionName = '';
        el.removeAttribute('data-vt-claimed');
      });
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-vt-product]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      clearNames();
      const img = link.querySelector('img');
      if (!img) return;
      img.style.viewTransitionName = VT_NAME;
      img.setAttribute('data-vt-claimed', '');
    });

    // Restoring from bfcache would otherwise leave a duplicate name behind.
    window.addEventListener('pageshow', clearNames);
  };

  /* --- Registration ------------------------------------------------------ */

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  define('hoa-header', HoaHeader);
  define('hoa-drawer', HoaDrawer);
  define('hoa-cart-drawer', HoaCartDrawer);
  define('hoa-product-form', HoaProductForm);
  define('hoa-quantity', HoaQuantity);
  define('hoa-variants', HoaVariants);
  define('hoa-facets', HoaFacets);
  define('hoa-slider', HoaSlider);
  define('hoa-sticky-buy', HoaStickyBuy);

  const boot = () => {
    initMediaFade();
    // Split before observing, so the reveal state lands on finished markup.
    const splitThenReveal = () => {
      initLineReveal();
      initReveal();
    };
    if (document.fonts?.ready) {
      document.fonts.ready.then(splitThenReveal);
    } else {
      splitThenReveal();
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    boot();
    initViewTransitions();
    reconcileScrollLock();
  });

  // Restoring from bfcache replays the old DOM, lock class and all. If no
  // drawer is actually open, release it rather than leaving a dead page.
  window.addEventListener('pageshow', reconcileScrollLock);

  // The theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', boot);

  HOA.refreshCart = refreshCart;
  HOA.announce = announce;
})();
